import {
    FETCH_BOOKINGS,
    FETCH_BOOKINGS_SUCCESS,
    FETCH_BOOKINGS_FAILED,
    FETCH_BOOKINGS_HISTORY,
    FETCH_BOOKINGS_HISTORY_SUCCESS,
    FETCH_BOOKINGS_HISTORY_FAILED,
    UPDATE_BOOKING,
    CANCEL_BOOKING,
    CLEAR_BOOKING,
    UPDATE_BOOKING_IMAGE,
} from "../store/types";
import { fetchBookingLocations } from "../actions/locationactions";
import {
    RequestPushMsg,
    sendNotificationOnce,
} from "../other/NotificationFunctions";
import store from "../store/store";
import { firebase } from "../config/configureFirebase";
import { get, onValue, update, off, remove, push } from "firebase/database";
import { uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { Platform } from "react-native";
import { addToWallet } from "./paymentactions";
import { getAdminConfiguration } from "./adminQueueActions";
import appConfig from "../../../config.json";

// Helper function to calculate driver rating from last 100 trips
export const calculateDriverRatingFromLast100Trips = (
    ratings,
    newRating = null
) => {
    let sum = 0;
    let count = 0;
    const MAX_TRIPS = 100;

    if (ratings) {
        // Convert ratings object to array
        const ratingsArray = Object.values(ratings);

        // Sort by timestamp (newest first)
        ratingsArray.sort((a, b) => {
            const dateA = a.timestamp
                ? new Date(a.timestamp).getTime()
                : a.date
                ? new Date(a.date).getTime()
                : 0;
            const dateB = b.timestamp
                ? new Date(b.timestamp).getTime()
                : b.date
                ? new Date(b.date).getTime()
                : 0;
            return dateB - dateA; // Descending order (newest first)
        });

        // Take only the last 100 trips (or less if not enough trips)
        const last100Trips = ratingsArray.slice(0, MAX_TRIPS);

        // Calculate sum of ratings
        for (let i = 0; i < last100Trips.length; i++) {
            sum += last100Trips[i].rate;
            count++;
        }
    }

    // Add new rating if provided
    if (newRating !== null) {
        sum = sum + newRating;
        count++;

        // If we now have more than 100 trips, we need to limit to 100
        if (count > MAX_TRIPS) {
            // Recalculate: take last 99 from existing + new rating
            sum = newRating;
            count = 1;
            if (ratings) {
                const ratingsArray = Object.values(ratings);
                ratingsArray.sort((a, b) => {
                    const dateA = a.timestamp
                        ? new Date(a.timestamp).getTime()
                        : a.date
                        ? new Date(a.date).getTime()
                        : 0;
                    const dateB = b.timestamp
                        ? new Date(b.timestamp).getTime()
                        : b.date
                        ? new Date(b.date).getTime()
                        : 0;
                    return dateB - dateA;
                });
                const last99Trips = ratingsArray.slice(0, MAX_TRIPS - 1);
                for (let i = 0; i < last99Trips.length; i++) {
                    sum += last99Trips[i].rate;
                    count++;
                }
            }
        }
    }

    // Calculate average from last 100 trips
    return count > 0 ? parseFloat(sum / count).toFixed(1) : newRating || "0";
};

export const fetchBookings = () => (dispatch) => {
    const { bookingListRef } = firebase;

    dispatch({
        type: FETCH_BOOKINGS,
        payload: null,
    });

    const userInfo = store.getState().auth.profile;

    off(bookingListRef(userInfo.uid, userInfo.usertype));
    onValue(bookingListRef(userInfo.uid, userInfo.usertype), (snapshot) => {
        if (snapshot.val()) {
            const data = snapshot.val();
            const active = [];
            let tracked = null;
            const bookings = Object.keys(data)
                .filter(
                    (key) =>
                        userInfo.usertype !== "fleetadmin" ||
                        (userInfo.usertype === "fleetadmin" &&
                            (data[key].fleetadmin === userInfo.uid ||
                                data[key].fleetadminrider === userInfo.uid))
                )
                .map((i) => {
                    data[i].id = i;
                    data[i].pickupAddress = data[i].pickup?.add ?? "";
                    data[i].dropAddress = data[i].drop?.add ?? "";
                    data[i].discount = data[i].discount ? data[i].discount : 0;
                    data[i].cashPaymentAmount = data[i].cashPaymentAmount
                        ? data[i].cashPaymentAmount
                        : 0;
                    data[i].cardPaymentAmount = data[i].cardPaymentAmount
                        ? data[i].cardPaymentAmount
                        : 0;
                    return data[i];
                });
            for (let i = 0; i < bookings.length; i++) {
                // Include ADMIN_QUEUE in active bookings so it stays in BookedCabScreen
                if (
                    [
                        "PAYMENT_PENDING",
                        "NEW",
                        "ACCEPTED",
                        "ARRIVED",
                        "STARTED",
                        "REACHED",
                        "PENDING",
                        "PAID",
                        "ADMIN_QUEUE",
                    ].indexOf(bookings[i].status) != -1
                ) {
                    active.push(bookings[i]);
                }
                if (
                    ["ACCEPTED", "ARRIVED", "STARTED"].indexOf(
                        bookings[i].status
                    ) != -1 &&
                    userInfo.usertype == "driver"
                ) {
                    tracked = bookings[i];
                    fetchBookingLocations(tracked.id)(dispatch);
                }
            }
            dispatch({
                type: FETCH_BOOKINGS_SUCCESS,
                payload: {
                    bookings: bookings.reverse(),
                    active: active,
                    tracked: tracked,
                },
            });
            if (tracked) {
                dispatch({
                    type: FETCH_BOOKINGS_SUCCESS,
                    payload: null,
                });
            }
        } else {
            dispatch({
                type: FETCH_BOOKINGS_FAILED,
                payload:
                    store.getState().languagedata.defaultLanguage.no_bookings,
            });
        }
    });
};

export const getBookingData = (bookingId) => async (dispatch) => {
    const { singleBookingRef } = firebase;
    const bookingData = await get(singleBookingRef(bookingId));
    return bookingData.val();
};

export const updateBookingDriverEstimate =
    (bookingId, driverEstimateData) => async (dispatch) => {
        const { singleBookingRef } = firebase;

        try {
            // Only update the driver estimate fields
            const updateData = {
                driverEstimateDistance:
                    driverEstimateData.driverEstimateDistance,
                driverEstimateTime: driverEstimateData.driverEstimateTime,
                driverEstimateUpdatedAt: new Date().toISOString(),
            };

            // console.log('🚗 Updating booking driver estimates:', bookingId, updateData); // Removed repetitive log

            // Update booking in Firebase
            await update(singleBookingRef(bookingId), updateData);

            dispatch({
                type: "UPDATE_BOOKING_DRIVER_ESTIMATE",
                payload: { bookingId, ...updateData },
            });
        } catch (error) {
            console.error("❌ Error updating booking driver estimate:", error);
        }
    };

export const updateBooking = (booking) => async (dispatch) => {
    const {
        auth,
        trackingRef,
        singleBookingRef,
        singleUserRef,
        settingsRef,
        userRatingsRef,
    } = firebase;

    dispatch({
        type: UPDATE_BOOKING,
        payload: booking,
    });

    const settingsdata = await get(settingsRef);
    const settings = settingsdata.val();

    if (booking.status == "PAYMENT_PENDING") {
        update(singleBookingRef(booking.id), booking);
    }
    if (booking.status == "NEW" || booking.status == "ACCEPTED") {
        if (booking.hasBid) {
            if (booking.driver && booking.prepaid) {
                booking.status = "ACCEPTED";
            }
            // FIXED: Only update driver queue for NEW bookings, not ACCEPTED bookings
            // This prevents trip from reappearing when user selects cash payment
            if (booking.status === "NEW") {
                // For BID bookings, use selectedBid.driver if available, otherwise use booking.driver
                const driverId =
                    booking.selectedBid && booking.selectedBid.driver
                        ? booking.selectedBid.driver
                        : booking.driver;
                if (driverId) {
                    update(singleUserRef(driverId), { queue: true });
                }
                // Send WhatsApp notification for NEW status (searching for driver)
                if (booking.customer_contact) {
                    try {
                        const whatsappMessage = `🔍 Searching for Driver\n\nYour booking has been confirmed and we are currently searching for the best available driver for you.\n\nBooking ID: ${
                            booking.id
                        }\nPickup: ${
                            booking.pickup?.add ||
                            booking.trip_start ||
                            "Your location"
                        }\nDestination: ${
                            booking.drop?.add ||
                            booking.trip_end ||
                            "Your destination"
                        }\n\nPlease wait while we find a driver. You will be notified once a driver accepts your trip.`;

                        // Clean phone number - remove + if present for the API
                        const phoneNumber = booking.customer_contact.replace(
                            "+",
                            ""
                        );

                        fetch(
                            `${appConfig.api_endpoints.whatsapp_ai_url}/wa/webhook-notification`,
                            {
                                method: "POST",
                                headers: {
                                    "x-key":
                                        "a5af540687e5f73a00ad4a6a7545b5cc1ba1fa4c",
                                    "Content-Type": "application/json",
                                },
                                body: JSON.stringify({
                                    number: phoneNumber,
                                    message: whatsappMessage,
                                }),
                            }
                        )
                            .then((response) => {
                                if (response.ok) {
                                    console.log(
                                        "✅ WhatsApp NEW status notification sent successfully"
                                    );
                                } else {
                                    console.log(
                                        "⚠️ WhatsApp NEW status notification failed:",
                                        response.status
                                    );
                                }
                            })
                            .catch((error) => {
                                console.log(
                                    "❌ WhatsApp NEW status notification error:",
                                    error
                                );
                            });
                    } catch (error) {
                        console.log(
                            "❌ Error sending WhatsApp NEW status notification:",
                            error
                        );
                    }
                }
            } else if (booking.status === "ACCEPTED" && booking.driver) {
                // For ACCEPTED bookings, ensure driver is removed from available list
                // This prevents the trip from appearing again in driver app
                update(singleUserRef(booking.driver), { queue: false });
                // Send WhatsApp notification for ACCEPTED status
                if (booking.customer_contact) {
                    try {
                        // Get driver details
                        get(singleUserRef(booking.driver))
                            .then((driverSnapshot) => {
                                const driverData = driverSnapshot.val();
                                if (driverData) {
                                    const driverEstimate =
                                        booking.driverEstimates &&
                                        booking.driverEstimates[booking.driver]
                                            ? booking.driverEstimates[
                                                  booking.driver
                                              ].timein_text
                                            : "5 min";

                                    const whatsappMessage = `Driver found! Here are the details:\n${
                                        driverData.firstName || ""
                                    } ${driverData.lastName || ""}\n⭐ ${
                                        driverData.rating || "5.0"
                                    } | 🚗 ${driverEstimate}\n🚙 ${
                                        driverData.vehicleMake || ""
                                    } ${driverData.vehicleModel || ""} | ${
                                        driverData.carType || ""
                                    }\n🚗 ${driverData.vehicleNumber || ""}`;

                                    // Clean phone number - remove + if present for the API
                                    const phoneNumber =
                                        booking.customer_contact.replace(
                                            "+",
                                            ""
                                        );

                                    fetch(
                                        `${appConfig.api_endpoints.whatsapp_ai_url}/wa/webhook-notification`,
                                        {
                                            method: "POST",
                                            headers: {
                                                "x-key":
                                                    "a5af540687e5f73a00ad4a6a7545b5cc1ba1fa4c",
                                                "Content-Type":
                                                    "application/json",
                                            },
                                            body: JSON.stringify({
                                                number: phoneNumber,
                                                message: whatsappMessage,
                                            }),
                                        }
                                    )
                                        .then((response) => {
                                            if (response.ok) {
                                                console.log(
                                                    "✅ WhatsApp ACCEPTED notification sent successfully"
                                                );
                                            } else {
                                                console.log(
                                                    "⚠️ WhatsApp ACCEPTED notification failed:",
                                                    response.status
                                                );
                                            }
                                        })
                                        .catch((error) => {
                                            console.log(
                                                "❌ WhatsApp ACCEPTED notification error:",
                                                error
                                            );
                                        });
                                }
                            })
                            .catch((error) => {
                                console.log(
                                    "❌ Error fetching driver data for WhatsApp notification:",
                                    error
                                );
                            });
                    } catch (error) {
                        console.log(
                            "❌ Error sending WhatsApp ACCEPTED notification:",
                            error
                        );
                    }
                }
            }
        }
        update(singleBookingRef(booking.id), booking);
    }

    // Special handling for card payment - don't change status if payment not completed
    if (
        booking.payment_mode == "card" &&
        booking.status == "NEW" &&
        !booking.payment_transaction_id
    ) {
        // Only update payment info, don't change status to preserve driver notifications
        const paymentInfo = {
            payment_mode: booking.payment_mode,
            customer_paid: booking.customer_paid,
            discount: booking.discount,
            usedWalletMoney: booking.usedWalletMoney,
            cardPaymentAmount: booking.cardPaymentAmount,
            cashPaymentAmount: booking.cashPaymentAmount,
            payableAmount: booking.payableAmount,
            promo_applied: booking.promo_applied,
            promo_details: booking.promo_details,
            paymentPacket: booking.paymentPacket,
        };
        update(singleBookingRef(booking.id), paymentInfo);

        // Send WhatsApp notification for NEW status (searching for driver)
        if (booking.customer_contact) {
            try {
                const whatsappMessage = `🔍 Searching for Driver\n\nYour booking has been confirmed and we are currently searching for the best available driver for you.\n\nBooking ID: ${
                    booking.id
                }\nPickup: ${
                    booking.pickup?.add || booking.trip_start || "Your location"
                }\nDestination: ${
                    booking.drop?.add || booking.trip_end || "Your destination"
                }\n\nPlease wait while we find a driver. You will be notified once a driver accepts your trip.`;

                // Clean phone number - remove + if present for the API
                const phoneNumber = booking.customer_contact.replace("+", "");

                fetch(
                    `${appConfig.api_endpoints.whatsapp_ai_url}/wa/webhook-notification`,
                    {
                        method: "POST",
                        headers: {
                            "x-key": "a5af540687e5f73a00ad4a6a7545b5cc1ba1fa4c",
                            "Content-Type": "application/json",
                        },
                        body: JSON.stringify({
                            number: phoneNumber,
                            message: whatsappMessage,
                        }),
                    }
                )
                    .then((response) => {
                        if (response.ok) {
                            console.log(
                                "✅ WhatsApp NEW status notification sent successfully"
                            );
                        } else {
                            console.log(
                                "⚠️ WhatsApp NEW status notification failed:",
                                response.status
                            );
                        }
                    })
                    .catch((error) => {
                        console.log(
                            "❌ WhatsApp NEW status notification error:",
                            error
                        );
                    });
            } catch (error) {
                console.log(
                    "❌ Error sending WhatsApp NEW status notification:",
                    error
                );
            }
        }
        return; // Don't proceed with status change
    }

    // Allow status change to PAID for card payment after successful payment
    if (
        booking.payment_mode == "card" &&
        booking.status == "PAID" &&
        booking.payment_transaction_id
    ) {
        // Update booking with PAID status
        update(singleBookingRef(booking.id), booking);

        // Send WhatsApp notification for NEW status (searching for driver)
        if (booking.customer_contact) {
            try {
                // get booking id
                const resBooking = await fetch(
                    `${appConfig.api_endpoints.whatsapp_ai_url}/booking/getBookingID/${booking.id}`,
                    {
                        headers: {
                            "x-key": "a5af540687e5f73a00ad4a6a7545b5cc1ba1fa4c",
                        },
                    }
                );
                const bookingData = await resBooking.json();
                const bookingId = bookingData.bookingId;

                const whatsappMessage = `💳 Payment Successful\n\nYour payment has been successfully processed. Your booking has been confirmed.\n\nBooking ID: ${bookingId}\nPickup: ${
                    booking.pickup?.add || booking.trip_start || "Your location"
                }\nDestination: ${
                    booking.drop?.add || booking.trip_end || "Your destination"
                }\n\nThank you for choosing our service!`;

                // Clean phone number - remove + if present for the API
                const phoneNumber = booking.customer_contact.replace("+", "");

                fetch(
                    `${appConfig.api_endpoints.whatsapp_ai_url}/wa/webhook-notification`,
                    {
                        method: "POST",
                        headers: {
                            "x-key": "a5af540687e5f73a00ad4a6a7545b5cc1ba1fa4c",
                            "Content-Type": "application/json",
                        },
                        body: JSON.stringify({
                            number: phoneNumber,
                            message: whatsappMessage,
                        }),
                    }
                )
                    .then((response) => {
                        if (response.ok) {
                            console.log(
                                "✅ WhatsApp NEW status notification sent successfully"
                            );
                        } else {
                            console.log(
                                "⚠️ WhatsApp NEW status notification failed:",
                                response.status
                            );
                        }
                    })
                    .catch((error) => {
                        console.log(
                            "❌ WhatsApp NEW status notification error:",
                            error
                        );
                    });
            } catch (error) {
                console.log(
                    "❌ Error sending WhatsApp NEW status notification:",
                    error
                );
            }
        }
    }
    if (booking.status == "ARRIVED") {
        let dt = new Date();
        booking.driver_arrive_time = dt.getTime().toString();
        update(singleBookingRef(booking.id), booking);
        if (booking.customer_token) {
            RequestPushMsg(booking.customer_token, {
                title: store.getState().languagedata.defaultLanguage
                    .notification_title,
                msg: store.getState().languagedata.defaultLanguage.driver_near,
                screen: "BookedCab",
                params: { bookingId: booking.id },
                channelId: "general",
                sound: Platform.OS === "android" ? "general" : "general.mp3", // Correct format for each platform
            });
        }
    }
    if (booking.status == "STARTED") {
        let dt = new Date();
        let localString =
            dt.getHours() + ":" + dt.getMinutes() + ":" + dt.getSeconds();
        let timeString = dt.getTime();
        booking.trip_start_time = localString;
        booking.startTime = timeString;
        update(singleBookingRef(booking.id), booking);

        const driverLocation = store.getState().gpsdata.location;

        push(trackingRef(booking.id), {
            at: new Date().getTime(),
            status: "STARTED",
            lat: driverLocation.lat,
            lng: driverLocation.lng,
        });
        if (booking.customer_token) {
            RequestPushMsg(booking.customer_token, {
                title: store.getState().languagedata.defaultLanguage
                    .notification_title,
                msg:
                    store.getState().languagedata.defaultLanguage
                        .driver_journey_msg + booking.reference,
                screen: "BookedCab",
                params: { bookingId: booking.id },
                channelId: "general",
                sound: Platform.OS === "android" ? "general" : "general.mp3", // Correct format for each platform
            });
        }
    }
    if (booking.status == "REACHED") {
        const driverLocation = store.getState().gpsdata.location;

        push(trackingRef(booking.id), {
            at: new Date().getTime(),
            status: "REACHED",
            lat: driverLocation.lat,
            lng: driverLocation.lng,
        });

        const end_time = new Date();
        const diff =
            (end_time.getTime() - parseFloat(booking.startTime)) / 1000;
        const totalTimeTaken = Math.abs(Math.round(diff));
        booking.trip_end_time =
            end_time.getHours() +
            ":" +
            end_time.getMinutes() +
            ":" +
            end_time.getSeconds();
        booking.endTime = end_time.getTime();
        booking.total_trip_time = totalTimeTaken;

        update(singleBookingRef(booking.id), booking);
        if (booking.customer_token) {
            RequestPushMsg(booking.customer_token, {
                title: store.getState().languagedata.defaultLanguage
                    .notification_title,
                msg: store.getState().languagedata.defaultLanguage
                    .driver_completed_ride,
                screen: "BookedCab",
                params: { bookingId: booking.id },
                channelId: "general",
                sound: Platform.OS === "android" ? "general" : "general.mp3", // Correct format for each platform
            });
        }
    }
    if (booking.status == "PENDING") {
        update(singleBookingRef(booking.id), booking);
        update(singleUserRef(booking.driver), { queue: false });
    }

    // NEW: Handle ADMIN_QUEUE status for no drivers available scenario
    if (booking.status == "ADMIN_QUEUE") {
        console.log(
            "🔄 Processing ADMIN_QUEUE status for booking:",
            booking.id
        );

        // Update booking in Firebase
        update(singleBookingRef(booking.id), booking);

        // Send notification to admin if needed
        if (booking.customer_token) {
            RequestPushMsg(booking.customer_token, {
                title:
                    store.getState().languagedata.defaultLanguage
                        .notification_title || "Admin Queue",
                msg:
                    store.getState().languagedata.defaultLanguage
                        .no_drivers_available_message ||
                    "Your trip has been sent to Admin for driver assignment",
                screen: "BookedCab",
                params: { bookingId: booking.id },
                channelId: "general",
                sound: Platform.OS === "android" ? "general" : "general.mp3",
            });
        }

        console.log("✅ ADMIN_QUEUE status processed successfully");
    }
    if (booking.status == "PAID") {
        if (booking.booking_from_web) {
            booking.status = "COMPLETE";
        }

        // // add money to driver wallet
        // console.log("✅ Adding money to driver wallet", booking.driver, booking.payment_mode);
        // if (booking.driver && booking.payment_mode?.toLowerCase() == "points") {
        //     dispatch(addToWallet(booking.driver, parseFloat(booking.driver_share), "Points Payment", booking.id));
        //     console.log("✅ Added money to driver wallet");
        // }

        update(singleBookingRef(booking.id), booking);
        if (
            booking.driver == auth.currentUser.uid &&
            (booking.prepaid ||
                booking.payment_mode == "cash" ||
                booking.payment_mode == "yappi" ||
                booking.payment_mode == "wallet" ||
                booking.payment_mode == "card" ||
                booking.payment_mode?.toLowerCase() == "points")
        ) {
            update(singleUserRef(booking.driver), { queue: false });
        }

        if (
            booking.customer_token &&
            booking.payment_mode !== "cash" &&
            booking.payment_mode !== "yappi"
        ) {
            sendNotificationOnce(
                booking.customer_token,
                {
                    title: store.getState().languagedata.defaultLanguage
                        .notification_title,
                    msg: store.getState().languagedata.defaultLanguage
                        .success_payment,
                    screen: "BookedCab",
                    params: { bookingId: booking.id },
                    channelId: "general",
                    sound:
                        Platform.OS === "android" ? "general" : "general.mp3", // Correct format for each platform
                },
                booking.id,
                "customer_payment_success"
            );
        }

        if (
            booking.driver_token &&
            booking.payment_mode !== "cash" &&
            booking.payment_mode !== "yappi"
        ) {
            console.log(
                "Sending notification to driver:",
                booking.driver_token
            );
            sendNotificationOnce(
                booking.driver_token,
                {
                    title: store.getState().languagedata.defaultLanguage
                        .notification_title,
                    msg: store.getState().languagedata.defaultLanguage
                        .success_payment,
                    screen: "BookedCab",
                    params: { bookingId: booking.id },
                    channelId: "general",
                    sound:
                        Platform.OS === "android" ? "general" : "general.mp3", // Correct format for each platform
                },
                booking.id,
                "driver_payment_success"
            );
        }
    }
    if (booking.status == "COMPLETE") {
        update(singleBookingRef(booking.id), booking);
        if (booking.rating) {
            if (booking.driver_token) {
                RequestPushMsg(booking.driver_token, {
                    title: store.getState().languagedata.defaultLanguage
                        .notification_title,
                    msg: store
                        .getState()
                        .languagedata.defaultLanguage.received_rating.toString()
                        .replace("X", booking.rating.toString()),
                    screen: "BookedCab",
                    params: { bookingId: booking.id },
                    channelId: "general",
                    sound:
                        Platform.OS === "android" ? "general" : "general.mp3", // Correct format for each platform
                });
            }

            onValue(
                userRatingsRef(booking.driver),
                (snapshot) => {
                    let ratings = snapshot.val();

                    // If useLastHundredRatingAverageDriverCalc === false, use manual logic, otherwise use calculateDriverRatingFromLast100Trips
                    const settings =
                        store.getState().settingsdata.settings || [];
                    const useLastHundredRatingAverageDriverCalcSetting =
                        settings.find(
                            (item) =>
                                item.key ===
                                "useLastHundredRatingAverageDriverCalc"
                        );
                    let rating;
                    if (
                        useLastHundredRatingAverageDriverCalcSetting &&
                        useLastHundredRatingAverageDriverCalcSetting.value ===
                            false
                    ) {
                        // Manual rating average logic
                        let ratings = snapshot.val();
                        if (ratings) {
                            let sum = 0;
                            const arr = Object.values(ratings);
                            for (let i = 0; i < arr.length; i++) {
                                sum = sum + arr[i].rate;
                            }
                            sum = sum + booking.rating;
                            rating = parseFloat(sum / (arr.length + 1)).toFixed(
                                1
                            );
                        } else {
                            rating = booking.rating;
                        }
                    } else {
                        // Fallback to using last 100 trips calculator
                        rating = calculateDriverRatingFromLast100Trips(
                            snapshot.val(),
                            booking.rating
                        );
                    }

                    update(singleUserRef(booking.driver), { rating: rating });
                    push(userRatingsRef(booking.driver), {
                        user: booking.customer,
                        rate: booking.rating,
                        timestamp: new Date().toISOString(),
                        date: new Date().toISOString().split("T")[0], // YYYY-MM-DD format
                    });
                },
                { onlyOnce: true }
            );
        }
    }
};

// NEW: Special action for moving booking to admin queue
export const moveToAdminQueue = (booking) => async (dispatch) => {
    const { singleBookingRef } = firebase;

    try {
        // Prepare updated booking data
        const updatedBooking = {
            ...booking,
            status: "ADMIN_QUEUE",
            noDriversReason: "User chose to wait for admin to find drivers",
            noDriversTimestamp: new Date().toISOString(),
            adminQueuePriority: "normal",
            adminQueueNotes: "Waiting for admin to find available drivers",
            estimatedWaitTime: "15-30 minutes",
            lastUpdated: new Date().toISOString(),
        };

        // Update in Firebase
        await update(singleBookingRef(booking.id), updatedBooking);

        // Update Redux store
        dispatch({
            type: UPDATE_BOOKING,
            payload: updatedBooking,
        });

        // Send notification to customer
        if (booking.customer_token) {
            RequestPushMsg(booking.customer_token, {
                title:
                    store.getState().languagedata.defaultLanguage
                        .notification_title || "Admin Queue",
                msg:
                    store.getState().languagedata.defaultLanguage
                        .no_drivers_available_message ||
                    "Your trip has been sent to Admin for driver assignment",
                screen: "BookedCab",
                params: { bookingId: booking.id },
                channelId: "general",
                sound: Platform.OS === "android" ? "general" : "general.mp3",
            });
        }

        // Send WhatsApp notification for ADMIN_QUEUE status
        if (booking.customer_contact) {
            try {
                const whatsappMessage = `⏳ Driver Assignment in Progress\n\nWe're having difficulty finding available drivers in your area at the moment. Don't worry - your booking has been forwarded to our admin team for manual driver assignment.\n\nBooking ID: ${
                    booking.id
                }\nPickup: ${
                    booking.pickup?.add || booking.trip_start || "Your location"
                }\nDestination: ${
                    booking.drop?.add || booking.trip_end || "Your destination"
                }\n\nOur team is working to assign a driver to your trip. You will be notified once a driver is assigned.\n\nThank you for your patience.`;

                // Clean phone number - remove + if present for the API
                const phoneNumber = booking.customer_contact.replace("+", "");

                fetch(
                    `${appConfig.api_endpoints.whatsapp_ai_url}/wa/webhook-notification`,
                    {
                        method: "POST",
                        headers: {
                            "x-key": "a5af540687e5f73a00ad4a6a7545b5cc1ba1fa4c",
                            "Content-Type": "application/json",
                        },
                        body: JSON.stringify({
                            number: phoneNumber,
                            message: whatsappMessage,
                        }),
                    }
                )
                    .then((response) => {
                        if (response.ok) {
                            console.log(
                                "✅ WhatsApp ADMIN_QUEUE notification sent successfully"
                            );
                        } else {
                            console.log(
                                "⚠️ WhatsApp ADMIN_QUEUE notification failed:",
                                response.status
                            );
                        }
                    })
                    .catch((error) => {
                        console.log(
                            "❌ WhatsApp ADMIN_QUEUE notification error:",
                            error
                        );
                    });
            } catch (error) {
                console.log(
                    "❌ Error sending WhatsApp ADMIN_QUEUE notification:",
                    error
                );
            }
        }

        // Send WhatsApp notification for ADMIN_QUEUE status
        if (booking.customer_contact) {
            try {
                const whatsappMessage = `⏳ Driver Assignment in Progress\n\nWe're having difficulty finding available drivers in your area at the moment. Don't worry - your booking has been forwarded to our admin team for manual driver assignment.\n\nBooking ID: ${
                    booking.id
                }\nPickup: ${
                    booking.pickup?.add || booking.trip_start || "Your location"
                }\nDestination: ${
                    booking.drop?.add || booking.trip_end || "Your destination"
                }\n\nOur team is working to assign a driver to your trip. You will be notified once a driver is assigned.\n\nThank you for your patience.`;

                // Clean phone number - remove + if present for the API
                const phoneNumber = booking.customer_contact.replace("+", "");

                fetch(
                    `${appConfig.api_endpoints.whatsapp_ai_url}/wa/webhook-notification`,
                    {
                        method: "POST",
                        headers: {
                            "x-key": "a5af540687e5f73a00ad4a6a7545b5cc1ba1fa4c",
                            "Content-Type": "application/json",
                        },
                        body: JSON.stringify({
                            number: phoneNumber,
                            message: whatsappMessage,
                        }),
                    }
                )
                    .then((response) => {
                        if (response.ok) {
                            console.log(
                                "✅ WhatsApp ADMIN_QUEUE notification sent successfully"
                            );
                        } else {
                            console.log(
                                "⚠️ WhatsApp ADMIN_QUEUE notification failed:",
                                response.status
                            );
                        }
                    })
                    .catch((error) => {
                        console.log(
                            "❌ WhatsApp ADMIN_QUEUE notification error:",
                            error
                        );
                    });
            } catch (error) {
                console.log(
                    "❌ Error sending WhatsApp ADMIN_QUEUE notification:",
                    error
                );
            }
        }

        // 🚀 SEND NOTIFICATION TO DRIVERS when order enters admin queue
        try {
            // Import admin queue actions
            const { sendAdminQueueNotification } = await import(
                "./adminQueueActions"
            );

            // Send notification to available drivers
            await sendAdminQueueNotification(booking.id, updatedBooking);

            console.log("🔔 Driver notification sent successfully");
        } catch (notificationError) {
            console.error(
                "❌ Error sending driver notification:",
                notificationError
            );
            // Don't fail the whole operation if notification fails
        }

        return { success: true, booking: updatedBooking };
    } catch (error) {
        console.error("❌ Error moving booking to admin queue:", error);

        dispatch({
            type: FETCH_BOOKINGS_FAILED,
            payload: "Failed to move booking to admin queue",
        });

        return { success: false, error: error.message };
    }
};

export const cancelBooking = (data) => async (dispatch, getState) => {
    const {
        singleBookingRef,
        singleUserRef,
        requestedDriversRef,
        queueSettingsRef,
    } = firebase;

    dispatch({
        type: CANCEL_BOOKING,
        payload: data,
    });

    // Get automatic search setting from Firebase queue_settings
    let automaticSearchEnabled = false;
    try {
        const queueSettings = await getAdminConfiguration();
        automaticSearchEnabled = queueSettings.automaticSearch === true;
        console.log(
            "🔍 Queue Settings automaticSearch:",
            automaticSearchEnabled
        );
    } catch (error) {
        console.error("❌ Error fetching queue settings:", error);
        automaticSearchEnabled = false;
    }

    // Check if this is a driver cancellation and auto search should be enabled
    // Auto search should be enabled only if admin config allows it
    const shouldEnableAutoSearch =
        data.cancelledBy === "driver" &&
        data.booking.status === "ACCEPTED" &&
        automaticSearchEnabled;

    // Determine the new status based on auto search settings
    // If driver cancels but auto search is disabled, status should be CANCELLED
    const newStatus = shouldEnableAutoSearch ? "NEW" : "CANCELLED";

    // If auto search is enabled, mark the booking as previously accepted
    const updateData = {
        status: newStatus,
        reason: data.reason,
        cancelledBy: data.cancelledBy,
    };

    if (shouldEnableAutoSearch) {
        updateData.wasAccepted = true;
        updateData.previousDriver = data.booking.driver;
        updateData.previousDriverToken = data.booking.driver_token;
        console.log(
            "🔄 Auto Search: Driver cancelled accepted trip, keeping booking in NEW status for auto search"
        );
    }

    console.log("updateData", updateData);
    update(singleBookingRef(data.booking.id), updateData).then(() => {
        if (
            data.booking.driver &&
            (data.booking.status === "NEW" ||
                data.booking.status === "ACCEPTED" ||
                data.booking.status === "ARRIVED")
        ) {
            update(singleUserRef(data.booking.driver), { queue: false });
            if (data.booking.driver_token) {
                RequestPushMsg(data.booking.driver_token, {
                    title: store.getState().languagedata.defaultLanguage
                        .notification_title,
                    msg:
                        store.getState().languagedata.defaultLanguage
                            .booking_cancelled + data.booking.id,
                    screen: "BookedCab",
                    params: { bookingId: data.booking.id },
                    channelId: "general",
                    sound:
                        Platform.OS === "android" ? "general" : "general.mp3", // Correct format for each platform
                });
            }
            if (data.booking.customer_token) {
                RequestPushMsg(data.booking.customer_token, {
                    title: store.getState().languagedata.defaultLanguage
                        .notification_title,
                    msg:
                        store.getState().languagedata.defaultLanguage
                            .booking_cancelled + data.booking.id,
                    screen: "BookedCab",
                    params: { bookingId: data.booking.id },
                    channelId: "general",
                    sound:
                        Platform.OS === "android" ? "general" : "general.mp3", // Correct format for each platform
                });
            }
        }
        if (data.booking.status === "NEW") {
            remove(requestedDriversRef(data.booking.id));
        }
    });

    // Send WhatsApp notification for CANCELLED status
    if (data.booking.customer_contact && newStatus === "CANCELLED") {
        try {
            let whatsappMessage = "";

            // get booking id
            const resBooking = await fetch(
                `${appConfig.api_endpoints.whatsapp_ai_url}/booking/getBookingID/${data.booking.id}`,
                {
                    headers: {
                        "x-key": "a5af540687e5f73a00ad4a6a7545b5cc1ba1fa4c",
                    },
                }
            );
            const bookingData = await resBooking.json();
            const bookingId = bookingData.bookingId;

            // Determine cancellation message based on who cancelled
            if (data.cancelledBy === "driver") {
                whatsappMessage = `🚫 Trip Cancelled\n\nYour trip has been cancelled by the driver.\nReason: ${
                    data.reason || "Driver unavailable"
                }\n\nBooking ID: ${bookingId}\n\nWe apologize for the inconvenience. Please book again.`;
            } else if (data.cancelledBy === "customer") {
                whatsappMessage = `✅ Trip Cancelled\n\nYour trip has been successfully cancelled.\nReason: ${
                    data.reason || "Cancelled by customer"
                }\n\nBooking ID: ${bookingId}\n\nThank you for using our service.`;
            } else if (data.cancelledBy === "admin") {
                whatsappMessage = `🚫 Trip Cancelled\n\nYour trip has been cancelled by admin.\nReason: ${
                    data.reason || "Administrative cancellation"
                }\n\nBooking ID: ${bookingId}\n\nFor assistance, please contact support.`;
            } else {
                whatsappMessage = `🚫 Trip Cancelled\n\nYour trip has been cancelled.\nReason: ${
                    data.reason || "Trip cancelled"
                }\n\nBooking ID: ${bookingId}`;
            }

            // Clean phone number - remove + if present for the API
            const phoneNumber = data.booking.customer_contact.replace("+", "");

            fetch(
                `${appConfig.api_endpoints.whatsapp_ai_url}/wa/webhook-notification`,
                {
                    method: "POST",
                    headers: {
                        "x-key": "a5af540687e5f73a00ad4a6a7545b5cc1ba1fa4c",
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        number: phoneNumber,
                        message: whatsappMessage,
                    }),
                }
            )
                .then((response) => {
                    if (response.ok) {
                        console.log(
                            "✅ WhatsApp CANCELLED notification sent successfully"
                        );
                    } else {
                        console.log(
                            "⚠️ WhatsApp CANCELLED notification failed:",
                            response.status
                        );
                    }
                })
                .catch((error) => {
                    console.log(
                        "❌ WhatsApp CANCELLED notification error:",
                        error
                    );
                });
        } catch (error) {
            console.log(
                "❌ Error sending WhatsApp CANCELLED notification:",
                error
            );
        }
    }
};

// PowerTranz specific cancellation with refund processing
export const cancelPowerTranzBooking = (
    bookingId,
    cancelledBy = "customer",
    language = "en"
) => {
    return async (dispatch, getState) => {
        try {
            dispatch({
                type: "CANCEL_POWERTRANZ_BOOKING_REQUEST",
                payload: { bookingId, cancelledBy },
            });

            const { config } = firebase;
            const serverUrl = `https://us-central1-${config.projectId}.cloudfunctions.net`;

            console.log("🔄 Processing PowerTranz booking cancellation:", {
                bookingId,
                cancelledBy,
                language,
                endpoint: `${serverUrl}/powertranz-refund`,
            });

            const response = await fetch(`${serverUrl}/powertranz-refund`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    booking_id: bookingId,
                    cancelled_by: cancelledBy,
                    language: language,
                }),
            });

            // Check if response is ok
            if (!response.ok) {
                const errorText = await response.text();
                console.error(
                    "❌ Server response not ok:",
                    response.status,
                    errorText
                );
                throw new Error(
                    `Server error: ${response.status}. Please try again later.`
                );
            }

            // Check if response is JSON
            const contentType = response.headers.get("content-type");
            if (!contentType || !contentType.includes("application/json")) {
                const errorText = await response.text();
                console.error(
                    "❌ Response is not JSON:",
                    contentType,
                    errorText
                );
                throw new Error("Server error. Please try again later.");
            }

            const result = await response.json();

            console.log("📥 PowerTranz refund result:", result);

            if (result.success) {
                dispatch({
                    type: "CANCEL_POWERTRANZ_BOOKING_SUCCESS",
                    payload: {
                        bookingId,
                        cancellationResult: result,
                        cancelledBy,
                    },
                });

                // Also dispatch regular cancel booking to update UI
                const state = getState();
                const booking = state.bookinglistdata.active?.find(
                    (b) => b.id === bookingId
                );
                if (booking) {
                    await dispatch(
                        cancelBooking({
                            booking: booking,
                            reason:
                                result.cancellation_reason ||
                                "PowerTranz cancellation",
                            cancelledBy: cancelledBy,
                        })
                    );
                }

                return result;
            } else {
                throw new Error(
                    result.error || "PowerTranz cancellation failed"
                );
            }
        } catch (error) {
            console.error("❌ PowerTranz cancellation error:", error);

            dispatch({
                type: "CANCEL_POWERTRANZ_BOOKING_FAILURE",
                payload: {
                    bookingId,
                    error: error.message,
                },
            });

            throw error;
        }
    };
};

// Auto Search specific cancellation - keeps booking in NEW status for auto search
export const cancelBookingForAutoSearch = (data) => (dispatch, getState) => {
    console.log("🔄 cancelBookingForAutoSearch called with data:", data);

    const { singleBookingRef, singleUserRef, requestedDriversRef, config } =
        firebase;

    dispatch({
        type: "CANCEL_BOOKING_FOR_AUTO_SEARCH",
        payload: data,
    });

    // Get current state to check auto search settings
    const state = getState();
    const customerProfile = state.auth?.profile;

    // This action is specifically for auto search, so always keep in NEW status
    const updateData = {
        status: "NEW",
        reason: data.reason,
        cancelledBy: data.cancelledBy,
        wasAccepted: true,
        previousDriver: data.booking.driver,
        previousDriverToken: data.booking.driver_token,
        automaticSearch: true,
    };

    console.log(
        "🔄 Auto Search: Driver cancelled accepted trip, keeping booking in NEW status for auto search"
    );

    update(singleBookingRef(data.booking.id), updateData).then(() => {
        // Remove driver from queue
        if (data.booking.driver) {
            update(singleUserRef(data.booking.driver), { queue: false });

            // Notify driver about cancellation
            if (data.booking.driver_token) {
                RequestPushMsg(data.booking.driver_token, {
                    title: store.getState().languagedata.defaultLanguage
                        .notification_title,
                    msg:
                        store.getState().languagedata.defaultLanguage
                            .booking_cancelled + data.booking.id,
                    screen: "BookedCab",
                    params: { bookingId: data.booking.id },
                    channelId: "general",
                    sound:
                        Platform.OS === "android" ? "general" : "general.mp3",
                });
            }
        }

        // Notify customer about cancellation and auto search
        if (data.booking.customer_token) {
            RequestPushMsg(data.booking.customer_token, {
                title: store.getState().languagedata.defaultLanguage
                    .notification_title,
                msg: "Driver cancelled your trip. Auto search is now active to find a new driver.",
                screen: "BookedCab",
                params: { bookingId: data.booking.id },
                channelId: "general",
                sound: Platform.OS === "android" ? "general" : "general.mp3",
                channelId: "general",
            });
        }

        // Remove requested drivers for this booking
        remove(requestedDriversRef(data.booking.id));
    });
};

export const updateBookingImage =
    (booking, imageType, imageBlob) => async (dispatch) => {
        const { singleBookingRef, bookingImageRef } = firebase;
        await uploadBytesResumable(
            bookingImageRef(booking.id, imageType),
            imageBlob
        )
            .then(() => {
                imageBlob.close();
                return getDownloadURL(bookingImageRef(booking.id, imageType));
            })
            .then((url) => {
                if (imageType == "pickup_image") {
                    booking.pickup_image = url;
                }
                if (imageType == "deliver_image") {
                    booking.deliver_image = url;
                }
                update(singleBookingRef(booking.id), booking);
                dispatch({
                    type: UPDATE_BOOKING,
                    payload: booking,
                });
            });
    };

// Auto Search action - finds and assigns available drivers to booking
export const searchForAvailableDrivers =
    (booking) => async (dispatch, getState) => {
        console.log(
            "🔄 Auto Search: Starting search for available drivers for booking:",
            booking.id
        );

        const {
            singleBookingRef,
            settingsRef,
            usersRef,
            allLocationsRef,
            requestedDriversRef,
        } = firebase;

        try {
            // Import the helper function from adminQueueActions
            const { getAvailableDrivers } = await import("./adminQueueActions");

            // Get settings for driver radius
            const settingsSnapshot = await get(settingsRef);
            const settings = settingsSnapshot.val() || {};
            const driverRadius = settings.driverRadius || 10;

            console.log(
                "🔍 Auto Search: Searching for drivers within radius:",
                driverRadius,
                "km"
            );

            // Find available drivers
            const availableDrivers = await getAvailableDrivers(
                booking.pickup,
                driverRadius
            );

            if (availableDrivers && availableDrivers.length > 0) {
                console.log(
                    "✅ Auto Search: Found",
                    availableDrivers.length,
                    "available drivers"
                );

                // 🚫 EXCLUDE PREVIOUS DRIVER: Filter out the driver who cancelled
                const previousDriverId =
                    booking.previousDriver || booking.driver;
                const filteredDrivers = availableDrivers.filter((driver) => {
                    const shouldExclude = driver.id === previousDriverId;
                    if (shouldExclude) {
                        console.log(
                            "🚫 Auto Search: Excluding previous driver:",
                            driver.id,
                            driver.name || "Unknown"
                        );
                    }
                    return !shouldExclude;
                });

                console.log(
                    "✅ Auto Search: After excluding previous driver:",
                    filteredDrivers.length,
                    "drivers remaining"
                );

                if (filteredDrivers.length === 0) {
                    console.log(
                        "❌ Auto Search: No drivers available after excluding previous driver"
                    );
                    return {
                        success: false,
                        error: "No available drivers found (previous driver excluded)",
                    };
                }

                // Reset booking data for new search
                const resetBookingData = {
                    status: "NEW",
                    requestedDrivers: {},
                    driverEstimates: {},
                    driverOffers: null,
                    driver: null,
                    driver_token: null,
                    selectedBid: null,
                    autoSearchActive: true,
                    autoSearchTimestamp: new Date().toISOString(),
                };

                // Add each available driver to requestedDrivers (excluding previous driver)
                filteredDrivers.forEach((driver) => {
                    resetBookingData.requestedDrivers[driver.id] = true;
                    resetBookingData.driverEstimates[driver.id] = {
                        distance: driver.distance,
                        timein_text:
                            (driver.distance * 2 + 1).toFixed(0) + " min",
                    };
                });

                // Update booking in Firebase
                await update(singleBookingRef(booking.id), resetBookingData);

                // Send notifications to all available drivers (excluding previous driver)
                const language = store.getState().languagedata.defaultLanguage;

                for (const driver of filteredDrivers) {
                    if (driver.token) {
                        console.log(
                            "🔔 Auto Search: Sending notification to driver:",
                            driver.id
                        );

                        // RequestPushMsg(driver.token, {
                        //     title: language.notification_title,
                        //     msg: language.new_booking_notification,
                        //     screen: "DriverTrips",
                        //     channelId: "bookings-repeat",
                        //     ios: driver.userPlatform === "IOS" ? true : false,
                        //     sound: "repeat.wav",
                        // });
                    }
                }

                // Update Redux store
                dispatch({
                    type: UPDATE_BOOKING,
                    payload: { ...booking, ...resetBookingData },
                });

                console.log(
                    "✅ Auto Search: Successfully sent booking to",
                    filteredDrivers.length,
                    "drivers (previous driver excluded)"
                );
                return { success: true, driversFound: filteredDrivers.length };
            } else {
                console.log("❌ Auto Search: No available drivers found");
                return { success: false, error: "No available drivers found" };
            }
        } catch (error) {
            console.error(
                "❌ Auto Search: Error searching for drivers:",
                error
            );
            return { success: false, error: error.message };
        }
    };

export const forceEndBooking = (booking) => async (dispatch) => {
    const {
        trackingRef,
        singleBookingRef,
        singleUserRef,
        walletHistoryRef,
        settingsRef,
    } = firebase;

    dispatch({
        type: UPDATE_BOOKING,
        payload: booking,
    });

    if (booking.status == "STARTED") {
        push(trackingRef(booking.id), {
            at: new Date().getTime(),
            status: "REACHED",
            lat: booking.drop.lat,
            lng: booking.drop.lng,
        });

        const end_time = new Date();
        const diff =
            (end_time.getTime() - parseFloat(booking.startTime)) / 1000;
        const totalTimeTaken = Math.abs(Math.round(diff));
        booking.trip_end_time =
            end_time.getHours() +
            ":" +
            end_time.getMinutes() +
            ":" +
            end_time.getSeconds();
        booking.endTime = end_time.getTime();
        booking.total_trip_time = totalTimeTaken;

        if (booking.customer_token) {
            RequestPushMsg(booking.customer_token, {
                title: store.getState().languagedata.defaultLanguage
                    .notification_title,
                msg: store.getState().languagedata.defaultLanguage
                    .driver_completed_ride,
                screen: "BookedCab",
                params: { bookingId: booking.id },
                channelId: "general",
                sound: Platform.OS === "android" ? "general" : "general.mp3", // Correct format for each platform
            });
        }

        update(singleUserRef(booking.driver), { queue: false });

        if (booking.prepaid) {
            const settingsdata = await get(settingsRef);
            const settings = settingsdata.val();

            onValue(
                singleUserRef(booking.driver),
                (snapshot) => {
                    let walletBalance = parseFloat(
                        snapshot.val().walletBalance
                    );
                    walletBalance =
                        walletBalance + parseFloat(booking.driver_share);
                    if (parseFloat(booking.cashPaymentAmount) > 0) {
                        walletBalance =
                            walletBalance -
                            parseFloat(booking.cashPaymentAmount);
                    }
                    update(singleUserRef(booking.driver), {
                        walletBalance: parseFloat(
                            walletBalance.toFixed(settings.decimal)
                        ),
                    });

                    let details = {
                        type: "Credit",
                        amount: parseFloat(booking.driver_share).toFixed(
                            settings.decimal
                        ),
                        date: new Date().getTime(),
                        txRef: booking.id,
                    };
                    push(walletHistoryRef(booking.driver), details);

                    if (parseFloat(booking.cashPaymentAmount) > 0) {
                        let details = {
                            type: "Debit",
                            amount: booking.cashPaymentAmount,
                            date: new Date().getTime(),
                            txRef: booking.id,
                        };
                        push(walletHistoryRef(booking.driver), details);
                    }
                },
                { onlyOnce: true }
            );

            if (booking.customer_token) {
                sendNotificationOnce(
                    booking.customer_token,
                    {
                        title: store.getState().languagedata.defaultLanguage
                            .notification_title,
                        msg: store.getState().languagedata.defaultLanguage
                            .success_payment,
                        screen: "BookedCab",
                        params: { bookingId: booking.id },
                        channelId: "general",
                        sound:
                            Platform.OS === "android"
                                ? "general"
                                : "general.mp3", // Correct format for each platform
                    },
                    booking.id,
                    "customer_payment_success"
                );
            }

            if (booking.driver_token) {
                sendNotificationOnce(
                    booking.driver_token,
                    {
                        title: store.getState().languagedata.defaultLanguage
                            .notification_title,
                        msg: store.getState().languagedata.defaultLanguage
                            .success_payment,
                        screen: "BookedCab",
                        params: { bookingId: booking.id },
                        channelId: "general",
                        sound:
                            Platform.OS === "android"
                                ? "general"
                                : "general.mp3", // Correct format for each platform
                    },
                    booking.id,
                    "driver_payment_success"
                );
            }
            booking.status = "PAID";
        } else {
            booking.status = "PENDING";
        }

        update(singleBookingRef(booking.id), booking);
    }
};
